/**
 * Rider shadow — a REAL shadow, cast by the real cyclist mesh under a real sun.
 *
 * OWNER DECISION (2026-07-31): "physically align the shape to the cyclist then
 * add a real light behind to the side and render a real shadow cast by the
 * light." This module replaces the old projected-silhouette quad
 * (render/riderShadow.ts, deleted) — a hand-warped CanvasTexture that never
 * quite read as the bike it was standing in for.
 *
 * HOW IT WORKS
 *  - An INVISIBLE CASTER PROXY: a clone of the RIGGED cyclist GLB
 *    (assets/models/cyclist_rig.glb — same art as cyclist.glb, legs split into
 *    thigh_L/R -> shin_L/R FK nodes; shared geometry, cloned materials), placed
 *    exactly where the rider is — wheels on the road,
 *    facing -Z, front wheel under the visible procedural front wheel. Every
 *    mesh gets `colorWrite = false` + `depthWrite = false` + `castShadow =
 *    true`, so the shadow map sees it and the colour pass does not.
 *  - The proxy is a CHILD of the rig's `leanFollower` group, itself a child of
 *    the camera rig root, whose x IS the interpolated player x, so it tracks
 *    the bike by construction. The rig root carries no curve offset (the player
 *    is at sim x; the road under z ~ 0 has zero curve offset), so no curve math
 *    is needed here. The follower sits at the root's origin (y = 0, the road)
 *    and is rotated each frame with the SAME roll + steer angles as the visible
 *    cockpit's `bikeLean` group, so the shadow tips into strafes about the tyre
 *    contact patch rather than standing bolt upright.
 *  - The sun (world.ts's DirectionalLight) is re-parented to the same rig root
 *    together with its target, so the orthographic shadow frustum follows the
 *    rider down the road. As of 2026-08-01 that frustum is no longer the ±6 m
 *    rider box: it is fitted to the whole visible CORRIDOR (see SHADOW_BOX
 *    below), because vehicles, buildings, trees and rails all cast now. This
 *    module still owns the sun because the rider shadow is the one shadow that
 *    must be pixel-right; everything else just rides along in the same map.
 *
 * WHY colorWrite=false WORKS HERE (verified empirically in Chrome/WebGPU,
 * 2026-07-31): the earlier note in ASSET_PIPELINE claimed three's WebGPU shadow
 * path was unusable. A live test — a MeshStandardMaterial cube with
 * `castShadow=true`, then `colorWrite=false` — cast a correct, correctly-shaped
 * shadow on the asphalt both before and after the cube itself vanished. The
 * broken case was the headless SwiftShader fallback, not WebGPU proper.
 *
 * SUN GEOMETRY (the reason the sun moved). The eye is 1.35 m up, pitched 7°
 * down, 78° vFOV, so the road only enters frame at z ~ -1.3 (see
 * `groundVisibleFrom`). The old sun sat 54° above the horizon: the rider's
 * shadow was barely a metre long and fell entirely BEHIND that cut, i.e. off
 * screen. The sun is now low (~30°) and mostly BEHIND the rider with a lateral
 * bias, so the silhouette stretches forward-left across the visible road,
 * head-first, exactly like real POV cycling footage into a low sun.
 *
 * PEDALLING (2026-07-31). A static silhouette gliding down the road reads as a
 * mannequin; the shadow is the only view the player has of their own body, so
 * the legs must work. The proxy is the RIGGED cyclist, and every render frame
 * a crank phase driven by ground travel is turned into hip/knee angles by a
 * two-link IK solver (render/pedalIK.ts). The crash cinematic keeps using the
 * un-rigged `registry.cyclist`, untouched.
 */

import {
  Group,
  Mesh,
  type DirectionalLight,
  type Material,
  type Object3D,
} from "three/webgpu";
import { TUNING } from "../sim/tuning";
import { WHEEL_Z } from "./cameraRig";
import { fitOrthoShadow } from "./shadowFrustum";
import { setShadowDepthRange, softShadowFilter } from "./softShadow";
import {
  LEG_SOLVER_L,
  LEG_SOLVER_R,
  RIG_NODES,
  advanceCrankPhase,
  solveLeg,
  type LegSolver,
} from "./pedalIK";

/** Minimal view of the camera rig: where to hang (the sun on the root, the
 *  proxy on the ground-level lean follower), and whether the crash cam has
 *  taken the rig over (root parked at the origin). */
export interface CasterHost {
  readonly root: Object3D;
  /**
   * Group at the rig root's origin, on the road surface (y = 0), that the rig
   * rotates every frame with the bike's roll + steer angles. The proxy hangs
   * off THIS (not the root) so the cast shadow leans and steers with the bike,
   * pivoting at the tyres. The sun deliberately stays on the root: it must not
   * rotate with the bike, or the silhouette would never change.
   */
  readonly leanFollower: Object3D;
  readonly crashCamEnabled: boolean;
}

// ---------------------------------------------------------------------------
// Sun placement
// ---------------------------------------------------------------------------

/**
 * Sun offset from its aim point, in metres, in the rig root's frame. Positive z
 * is BEHIND the rider (the player faces -Z) and positive x is to the rider's
 * RIGHT, so the shadow falls forward-LEFT — the design-spec direction, kept.
 *
 * The ratios are what matter: a ground point at height h lands its shadow
 * `z - (SUN.z/SUN.y)*h` down-road and `x - (SUN.x/SUN.y)*h` across. With
 * y:z = 10:16.5 the rider's head (1.7 m) throws its shadow 2.8 m ahead — well
 * past the z ~ -1.3 frame cut — and x:y = 4.1:10 slides it 0.7 m left, about
 * two thirds of the way to the frame edge at that depth. Elevation ~30°.
 */
export const SUN_OFFSET = { x: 4.1, y: 10, z: 16.5 } as const;

/** Where the sun aims, in the rig root's frame: between the rider and the far
 *  end of the cast, so the tight frustum is centred on what it must resolve. */
export const SUN_TARGET_Z = -1.5;

/**
 * WORLD SHADOW BOX (2026-08-01 "everything casts shadows" pass).
 *
 * The corridor slab, in the rig frame, that the single sun's shadow map has to
 * cover. It replaces the old ±6 m rider-only box: vehicles, buildings, trees
 * and rails all cast now, so the frustum has to span the road the player can
 * actually see rather than the two metres around the bike.
 *
 *  - x ±15 m: the road corridor (±4.6 m) plus the near roadside row the props
 *    stand on (city building faces sit at |x| 8-10 m). Wider than that and the
 *    caster is off-frame AND its shadow lands off-frame.
 *  - z -70 .. +20 m: the sun sits low and nearly DOWN-ROAD, so a shadow is a
 *    long streak running away from the camera; past ~70 m it is both fogged and
 *    only a few pixels tall. The 20 m behind covers casters whose shadow is
 *    thrown FORWARD into frame.
 *  - y 0 .. 26 m: caster height. A 26 m building throws its shadow ~43 m
 *    down-road and 11 m across at this sun elevation, i.e. right across the
 *    road; taller parts are deliberately clipped because every extra metre of
 *    box costs shadow-map resolution over the WHOLE scene — and the shipped
 *    numbers are the balance point where the rider's own silhouette still
 *    reads (see SHADOW_MAP_SIZE).
 *
 * BUDGET. Every metre of this box is paid for by every other shadow: the fit
 * below turns it into a ~54 × 73 m light-space rectangle, so a 2048 map buys
 * ~38 × 28 texels per metre. The pre-2026-08-01 rider-only box bought 170.
 * That is THE trade of this feature and it is why the box is not bigger.
 *
 * Kept in ONE place because two things read it: the frustum fit here, and the
 * biome caster culling (render/biomes.ts SHADOW_CAST_AHEAD_M) which switches
 * `castShadow` off for content chunks that fall outside it.
 */
export const SHADOW_BOX = {
  minX: -15,
  maxX: 15,
  // -1.3, not 0: the curved-earth world bend (render/earthCurve.ts) pushes the
  // road DOWN by d²/2R, and the far bottom corner of this box (z = -70,
  // |x| = 15) sits 1.17 m below y = 0 once bent. Without the allowance the far
  // end of the road falls outside the fitted ortho frustum and its shadows drop
  // out. It costs ~2% of the fitted rectangle, i.e. ~1% of shadow resolution.
  minY: -1.3,
  maxY: 26,
  minZ: -70,
  maxZ: 20,
} as const;

/** How far ahead (m) the shadow frustum reaches — the caster-culling horizon. */
export const SHADOW_AHEAD_M = -SHADOW_BOX.minZ;
/** How far behind (m) it reaches. */
export const SHADOW_BEHIND_M = SHADOW_BOX.maxZ;

/**
 * Shadow map resolution (square).
 *
 * The fitted frustum is ~54 × 73 m, so 2048 buys ~38 × 28 texels/m and a 1.9 m
 * bike is ~60 texels long — soft, but still a cyclist. 4096 doubles that and is
 * a one-line change; it was measured (see the perf notes in the commit) and
 * left off because the target is a mobile-class GPU, where a 4096² depth target
 * is 67 MB of resident memory and the shadow pass rasterises four times the
 * area for a difference the player only sees in a screenshot.
 */
export const SHADOW_MAP_SIZE = 2048;

/**
 * Shadow PENUMBRA, in shadow-map texels (2026-08-01 lighting pass) — now the
 * SOFT END of the contact-hardening ramp (2026-08-02).
 *
 * The filter is the PCSS-lite in render/softShadow.ts, which reads this value
 * straight off `shadow.radius` (three's own filters either ignore the dial or
 * spend too few taps on it — see that module) and uses it as the MAXIMUM disk
 * radius. A caster that touches its receiver now gets ~1 texel instead, which
 * is what put the pedalling feet back into the rider's shadow.
 *
 * A texel of the fitted world frustum is ~3.5 cm, so 5 texels is a ~18 cm
 * penumbra: a building's roofline edge 30 m away goes properly soft. It is no
 * longer applied to the rider's own silhouette at close range, so it is free to
 * stay where the 2026-08-01 pass measured it.
 */
export const SHADOW_RADIUS = 5;

/**
 * Normal-offset bias, retuned for the softening pass.
 *
 * The Vogel disk samples the depth map up to SHADOW_RADIUS texels AWAY from the
 * shaded point, so a surface at a grazing angle to the sun compares against
 * depths recorded up to 5 texels further along itself. That is exactly the
 * geometry that produces acne, and the tolerance it needs scales with the
 * filter radius — hence 0.06 -> 0.11, roughly in step with the 1-texel
 * effective penumbra going to 5. The cost is ~11 cm of peter-panning along the
 * normal, which is well under one shadow-map texel of the ~18 cm penumbra and
 * is not visible: checked on the flat road and on building faces in city,
 * bridge, forest and fields.
 */
export const SHADOW_NORMAL_BIAS = 0.11;

/**
 * How far up-sun the light is PLACED, in metres along the SUN_OFFSET direction.
 *
 * A directional light's position is otherwise irrelevant (only the direction
 * lights the scene) — but three builds the shadow camera at that position, so
 * it decides what is in FRONT of the shadow near plane. At the old 19.7 m the
 * sun sat INSIDE the new corridor box: a tall building 20 m behind the rider
 * was behind the light and got clipped out of the map, which popped its shadow
 * off exactly as it swept across the road. 140 m puts the whole box in front of
 * the light with room for the tallest caster, at the cost of a ~110 m ortho
 * depth range — plenty of precision for a 32-bit depth target.
 */
export const SUN_PLACEMENT_DISTANCE = 140;

/** Unit sun direction (from the aim point toward the sun). */
const SUN_DIR_LEN = Math.hypot(SUN_OFFSET.x, SUN_OFFSET.y, SUN_OFFSET.z);
/** Where the light object actually sits, in the rig frame. */
export const SUN_POSITION = {
  x: (SUN_OFFSET.x / SUN_DIR_LEN) * SUN_PLACEMENT_DISTANCE,
  y: (SUN_OFFSET.y / SUN_DIR_LEN) * SUN_PLACEMENT_DISTANCE,
  z: SUN_TARGET_Z + (SUN_OFFSET.z / SUN_DIR_LEN) * SUN_PLACEMENT_DISTANCE,
} as const;

/** The fitted ortho frustum for SHADOW_BOX (static: the box never moves). */
export const SHADOW_FRUSTUM = fitOrthoShadow(
  SUN_POSITION,
  { x: 0, y: 0, z: SUN_TARGET_Z },
  SHADOW_BOX,
);

/** Near/far of the shadow camera (from the fit; they bracket the aim point). */
export const SHADOW_NEAR = SHADOW_FRUSTUM.near;
export const SHADOW_FAR = SHADOW_FRUSTUM.far;
/** Kept for callers that want a single "how big is the box" number (m). */
export const SHADOW_EXTENT = Math.max(
  SHADOW_FRUSTUM.right - SHADOW_FRUSTUM.left,
  SHADOW_FRUSTUM.top - SHADOW_FRUSTUM.bottom,
) / 2;

// ---------------------------------------------------------------------------
// Caster placement
// ---------------------------------------------------------------------------

/** Cyclist GLB half-height (m): scene bbox y is ±0.85189 (gltf-transform). */
export const CYCLIST_HALF_H = 0.85189;
/** Cyclist GLB half-length (m): scene bbox z is ±0.95, the bike's length axis. */
export const CYCLIST_HALF_LEN = 0.95;
/**
 * Model yaw. The GLB faces +Z (verified in-engine: dropped ahead of the camera
 * it looks straight back at the rider), and the player rides toward -Z, so the
 * proxy is turned a half-turn.
 */
export const CASTER_YAW = Math.PI;
/**
 * Front-wheel z of the visible procedural front wheel. IMPORTED, not copied:
 * the cockpit pass of 2026-08-02 pulled the visible wheel in from -1.45 to
 * -0.85 (see cameraRig WHEEL_Z), and a hard-coded twin here would have left the
 * shadow's bike half a metre ahead of the one the player can see — the shadow
 * contradicting the cockpit is exactly the artifact this alignment exists to
 * prevent.
 */
const FRONT_WHEEL_Z = WHEEL_Z;
/** Proxy centre z: front tip (centre - half length) lands on FRONT_WHEEL_Z. */
export const CASTER_Z = FRONT_WHEEL_Z + CYCLIST_HALF_LEN;

// ---------------------------------------------------------------------------
// Pure geometry helpers (unit-tested; no three.js needed)
// ---------------------------------------------------------------------------

/**
 * Where a point's shadow lands on the road (y = 0), for a directional light
 * whose direction is given by an offset from its target.
 *
 * @param x  caster point, lateral (m).
 * @param y  caster point, height above the road (m).
 * @param z  caster point, down-road (m; -Z is ahead).
 * @param sun light offset from its aim point (only the ratios matter).
 */
export function shadowGroundPoint(
  x: number,
  y: number,
  z: number,
  sun: { x: number; y: number; z: number } = SUN_OFFSET,
): { x: number; z: number } {
  return { x: x - (sun.x / sun.y) * y, z: z - (sun.z / sun.y) * y };
}

/**
 * Depth (m ahead of the eye) at which the road first enters frame: the bottom
 * edge of the frustum sits (vFOV/2 + pitch) below the horizontal, so the eye
 * height divided by that tangent is the nearest visible ground point. Anything
 * the shadow does closer than this is invisible — which is exactly why the sun
 * had to be lowered.
 */
export function groundVisibleFrom(): number {
  const P = TUNING.player;
  const half = ((P.fovBase / 2 + P.cameraPitchDeg) * Math.PI) / 180;
  return P.cameraHeight / Math.tan(half);
}

// ---------------------------------------------------------------------------
// The caster
// ---------------------------------------------------------------------------

/**
 * Invisible cyclist proxy + the sun that lights it. Construct after the assets
 * have loaded; call `update()` once per render frame.
 */
export class RiderCaster {
  /** Wrapper holding the proxy clone; child of the rig root. */
  readonly group: Group;
  private readonly host: CasterHost;
  /** Materials this class cloned and therefore owns. */
  private readonly materials: Material[] = [];
  /** True when a cyclist GLB was supplied and the proxy exists. */
  readonly ready: boolean;
  /** The four FK leg nodes, when the proxy came from the RIGGED GLB. */
  private readonly legs: {
    readonly solver: LegSolver;
    readonly thigh: Object3D;
    readonly shin: Object3D;
  }[] = [];
  /** Crank angle (rad). 0 = the GLB's authored rest pose. */
  private crankPhase = 0;

  /** True when the proxy carries the four FK leg nodes and pedals. */
  get pedals(): boolean {
    return this.legs.length === 2;
  }

  /** Current crank angle (rad) — exposed for tests/debug. */
  get phase(): number {
    return this.crankPhase;
  }

  /**
   * @param host    camera rig (the proxy and the sun both hang off `host.root`,
   *                whose x is the interpolated player x every frame).
   * @param cyclist the loaded RIGGED cyclist GLB (registry.cyclistRig; the
   *                un-rigged registry.cyclist also works but cannot pedal).
   *                Null → no proxy; the sun is still configured (harmless,
   *                nothing casts).
   * @param sun     the scene's DirectionalLight (world.sunLight). Re-parented
   *                to the rig root so its shadow frustum tracks the rider.
   */
  constructor(host: CasterHost, cyclist: Group | null, sun: DirectionalLight) {
    this.host = host;
    this.group = new Group();
    this.group.name = "riderCaster";
    // Hang off the LEAN FOLLOWER, not the root: the follower carries the rig's
    // bike-lean roll/steer about a ground-level pivot, so the silhouette the
    // sun sees tips exactly like the cockpit the player sees.
    host.leanFollower.add(this.group);

    this.configureSun(sun, host.root);

    if (!cyclist) {
      this.ready = false;
      return;
    }

    // clone(true) shares geometry + materials with the registry copy; the crash
    // cinematic clones the SAME source, so the materials must be cloned here or
    // colorWrite=false would make the crash cyclist invisible too.
    const proxy = cyclist.clone(true);
    proxy.name = "riderCasterProxy";
    proxy.rotation.y = CASTER_YAW;
    proxy.position.set(0, CYCLIST_HALF_H, CASTER_Z);
    proxy.traverse((o: Object3D) => {
      const m = o as Mesh;
      if (!m.isMesh) return;
      m.castShadow = true;
      m.receiveShadow = false;
      // The proxy is always within a couple of metres of the eye; its bounds
      // are irrelevant and meshopt-quantized bounds are unreliable anyway.
      m.frustumCulled = false;
      const src = m.material as Material | Material[];
      const clone = (mat: Material): Material => {
        const c = mat.clone();
        // Draw nothing: no colour, no depth. The shadow pass uses its own
        // depth target and is unaffected by either flag.
        c.colorWrite = false;
        c.depthWrite = false;
        this.materials.push(c);
        return c;
      };
      m.material = Array.isArray(src) ? src.map(clone) : clone(src);
    });
    this.group.add(proxy);
    this.ready = true;

    // Pick up the FK leg nodes if this is the rigged GLB. `clone(true)` keeps
    // node names, so a name lookup on the CLONE (never the source) is enough.
    const thighL = proxy.getObjectByName(RIG_NODES.thighL);
    const shinL = thighL?.getObjectByName(RIG_NODES.shinL);
    const thighR = proxy.getObjectByName(RIG_NODES.thighR);
    const shinR = thighR?.getObjectByName(RIG_NODES.shinR);
    if (thighL && shinL && thighR && shinR) {
      this.legs.push(
        { solver: LEG_SOLVER_L, thigh: thighL, shin: shinL },
        { solver: LEG_SOLVER_R, thigh: thighR, shin: shinR },
      );
    } else {
      console.warn(
        "[riderCaster] no FK leg nodes on the caster proxy — the shadow will not pedal",
      );
    }
  }

  /**
   * Point the sun down-road from behind-right, hang it (and its target) off the
   * rig root so the frustum follows the rider, and give it a tight ortho box.
   */
  private configureSun(sun: DirectionalLight, root: Object3D): void {
    root.add(sun);
    root.add(sun.target);
    sun.target.position.set(0, 0, SUN_TARGET_Z);
    // Same DIRECTION as SUN_OFFSET, pushed back to SUN_PLACEMENT_DISTANCE so
    // the whole corridor box sits in front of the shadow near plane.
    sun.position.set(SUN_POSITION.x, SUN_POSITION.y, SUN_POSITION.z);
    sun.castShadow = true;
    sun.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
    const sc = sun.shadow.camera;
    // Fitted to the WORLD corridor box (see SHADOW_BOX), not to the rider: the
    // whole scene casts now. The frustum is asymmetric — the corridor runs away
    // from the light — so left/right and top/bottom come straight from the fit.
    sc.left = SHADOW_FRUSTUM.left;
    sc.right = SHADOW_FRUSTUM.right;
    sc.top = SHADOW_FRUSTUM.top;
    sc.bottom = SHADOW_FRUSTUM.bottom;
    sc.near = SHADOW_FRUSTUM.near;
    sc.far = SHADOW_FRUSTUM.far;
    // Inert until this is called — the frustum fields above only reach the GPU
    // through the projection matrix, which the shadow pass never recomputes.
    sc.updateProjectionMatrix();
    // Acne control, retuned for the world frustum. A texel is now ~3-5 cm
    // across (was ~0.6 cm) and the ortho depth range is ~120 m (was ~40 m), so
    // the constant bias — which is expressed in NORMALISED depth — buys ~2.4 cm
    // of world slop at -0.0002 and the rest of the job is done in normal space,
    // where the offset does not scale with the depth range. Measured clean on
    // the flat road at every biome, with no visible peter-panning under the
    // rider's tyres.
    sun.shadow.bias = -0.0002;
    sun.shadow.normalBias = SHADOW_NORMAL_BIAS;
    // SOFTNESS (2026-08-01 lighting pass, playtest: "nice soft shadows rather
    // than harsh simple ones"). The 16-tap Vogel-disk filter REPLACES whatever
    // `renderer.shadowMap.type` would have picked, for this light only — see
    // render/softShadow.ts for why none of the three built-ins fits.
    sun.shadow.radius = SHADOW_RADIUS;
    (sun.shadow as unknown as { filterNode: unknown }).filterNode = softShadowFilter;
    // CONTACT HARDENING (2026-08-02 playtest: "can't see the moving feet"). The
    // filter's blocker search measures the caster-to-receiver gap in NORMALIZED
    // depth; the ortho frustum is linear, so this one number is all it needs to
    // express the ramp in metres. Pushed in from here because the frustum fit
    // lives here, not in the filter.
    setShadowDepthRange(SHADOW_FRUSTUM.far - SHADOW_FRUSTUM.near);
  }

  /**
   * Per-render-frame update. The rig root carries the lateral tracking, so
   * there is nothing to translate here; this
   *  - hides the proxy while the crash cinematic owns the camera (the rig root
   *    parks at the world origin then, which would drop a phantom shadow in
   *    the middle of the road behind the real crash cyclist), and
   *  - advances the crank and re-solves both legs.
   *
   * The crank only turns while the run is `playing`: on the title screen and
   * while paused/crashed the wheels are not turning, so neither are the legs,
   * and the shadow holds the GLB's authored rest pose.
   *
   * @param dt           render frame time (s).
   * @param forwardSpeed rider ground speed (m/s).
   * @param playing      true only in the `playing` sim phase.
   */
  update(dt = 0, forwardSpeed = 0, playing = false): void {
    this.group.visible = !this.host.crashCamEnabled;
    if (playing && dt > 0) {
      this.crankPhase = advanceCrankPhase(this.crankPhase, forwardSpeed, dt);
      this.applyPedalPose();
    }
  }

  /** Reset the crank to the authored rest pose (called on a run restart). */
  reset(): void {
    this.crankPhase = 0;
    this.applyPedalPose();
  }

  /** Write the current crank phase into the four FK leg nodes. */
  private applyPedalPose(): void {
    for (const leg of this.legs) {
      const pose = solveLeg(leg.solver, this.crankPhase);
      leg.thigh.rotation.x = pose.thighX;
      leg.shin.rotation.x = pose.shinX;
    }
  }

  /** Free the materials this class cloned (geometry is shared — not ours). */
  dispose(): void {
    for (const m of this.materials) m.dispose();
    this.materials.length = 0;
    this.group.removeFromParent();
  }
}
